import { useId, type ReactNode } from 'react';
import { useCountUp } from '../motion/useCountUp.ts';
import { cx, formatNumber } from './util.ts';
import './base.css';
import './Stat.css';

export interface StatProps {
  label: ReactNode;
  value: number;
  decimals?: number;
  /** Unidad tras un espacio: «%», «€», «h». */
  unit?: string;
  /** Formato propio (sustituye a decimales y unidad). */
  format?: (value: number) => string;
  /** Contexto bajo la cifra: «de 42 documentos». */
  hint?: ReactNode;
  size?: 'lg' | 'display';
  /** false para mostrar la cifra sin conteo. */
  animate?: boolean;
  className?: string;
}

/**
 * Cifra grande de KPI con conteo animado (`--duration-count`). El lector de pantalla recibe
 * siempre el valor final; con movimiento reducido la cifra aparece ya en su valor.
 */
export function Stat({
  label,
  value,
  decimals = 0,
  unit,
  format,
  hint,
  size = 'lg',
  animate = true,
  className,
}: StatProps) {
  const id = useId();
  const counted = useCountUp(value);
  const shown = animate ? counted : value;
  const fmt = format ?? ((n: number) => `${formatNumber(n, decimals)}${unit ? ` ${unit}` : ''}`);

  return (
    <div role="group" aria-labelledby={id} className={cx('af-stat', `af-stat--${size}`, className)}>
      <span id={id} className="af-stat__label">
        {label}
      </span>
      <span className="af-stat__value" aria-hidden="true">
        {fmt(shown)}
      </span>
      <span className="af-sr-only">{fmt(value)}</span>
      {hint && <span className="af-stat__hint">{hint}</span>}
    </div>
  );
}

/** Alias con el nombre de la tarea: cifra grande. */
export const BigNumber = Stat;
